import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import ReviewSlider from '../components/ReviewSlider'

export default function Home() {
  useEffect(() => {
    document.title = 'Borgs Køreskole I/S'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Borgs Køreskole I/S i Kalundborg. Professionel køreundervisning, lovpakke kat. B, teori og trygge rammer for kommende køreskoleelever.'
      )
    } else {
      const meta = document.createElement('meta')
      meta.name = 'description'
      meta.content =
        'Borgs Køreskole I/S i Kalundborg. Professionel køreundervisning, lovpakke kat. B, teori og trygge rammer for kommende køreskoleelever.'
      document.head.appendChild(meta)
    }
  }, [])

  // Scraped hero car asset or modern high quality driving car
  const heroCarImage = 'https://assets.auxodev.dk/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/hero-ny-118.jpg'
  const modernCarImage = 'https://assets.auxodev.dk/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/modern-danish-driving-school-car.png'

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800">
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-white to-slate-50 pt-12 pb-20 lg:pt-20 lg:pb-32 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Hero Text Content */}
            <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
              <h1 className="font-header text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.15]">
                Kørekort med tryghed &amp; høj beståelsesprocent
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Velkommen til <strong>Borgs Køreskole I/S</strong>. Vi tilbyder moderne undervisning, tålmodige instruktører og fleksible teorihold. Tag dit kørekort i et behageligt tempo.
              </p>

              {/* CTAs */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link
                  to="/lovpakke-priser"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-base transition-all duration-200 active:scale-[0.98]"
                >
                  <span>Tilmeld dig næste hold</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </Link>

                <Link
                  to="/lovpakke-priser"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-white hover:bg-slate-100 text-slate-900 font-bold text-base border-2 border-slate-200 hover:border-emerald-500 transition-all duration-200"
                >
                  <i className="fa-solid fa-tags text-emerald-600" aria-hidden="true"></i>
                  <span>Se lovpakker og priser</span>
                </Link>
              </div>

              {/* Key trust bullets */}
              <div className="pt-6 border-t border-slate-200/80 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs sm:text-sm font-semibold text-slate-700">
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  29 Lektioner Teori
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  Manøvrebane &amp; Glatbane
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-base" aria-hidden="true"></i>
                  Grøn Skolebil
                </span>
              </div>

              {/* DKU Member Badge */}
              <div className="pt-5 flex items-center justify-center lg:justify-start gap-4">
                <img
                  src="/_assets/dku-logo.webp"
                  alt="Dansk Kørelærer-Union (DKU) Medlem"
                  className="h-16 w-16 sm:h-20 sm:w-20 object-contain shrink-0"
                />
                <div className="text-left">
                  <div className="text-sm sm:text-base font-bold text-slate-900">Medlem af Dansk Kørelærer-Union</div>
                  <div className="text-xs sm:text-sm text-slate-600">Din garanti for godkendt, professionel køreundervisning og tryghed</div>
                </div>
              </div>
            </div>

            {/* Hero Image - Preserving the Central Green Driving Car */}
            <div className="lg:col-span-6 relative p-0 m-0">
              <div className="relative w-full overflow-hidden">
                <img
                  src={heroCarImage}
                  onError={(e) => { e.target.src = modernCarImage }}
                  alt="Den kendte grønne skolebil - Borgs Køreskole I/S"
                  className="w-full h-[350px] sm:h-[450px] lg:h-[480px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4 REDESIGNED INFO BOXES (VISUALLY HIGH HIGHERARCHY, LARGER, CLICKABLE, FA ICONS) */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Kort 1: Tilmelding */}
            <a
              href="https://gondrive.com/api/site/school/96/iframe/3?license=B"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-emerald-900 text-white p-8 border-2 border-emerald-800 transition-all duration-300 flex flex-col justify-between shadow-md hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-white">
                    <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-white mb-3">
                  Tilmelding
                </h3>
                <p className="text-white text-sm leading-relaxed mb-6 transition-colors">
                  Nu kan du booke en plads i køreskolen her. Gennem hele undervisningen sætter jeg den enkelte elev i centrum.
                </p>
              </div>

              <div className="pt-4 border-t border-emerald-800 flex items-center justify-between text-sm font-bold text-white transition-colors">
                <span>Tilmeld dig i dag</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

            {/* Kort 2: Priser */}
            <Link
              to="/lovpakke-priser"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-tags" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Priser
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Klik dig videre og se vores priser på kørekort til dig, som vil have det billigt og sjovere end andre køreskoler i byen.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>Se vores priser</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </Link>

            {/* Kort 3: Generhverv */}
            <Link
              to="/kontakt"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-id-card" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Generhverv
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Har du mistet dit kørekort? Nu kan du nemt og diskret tage dit kørekort på ny.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>Læs mere i dag</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </Link>

            {/* Kort 4: Log ind */}
            <a
              href="https://gondrive.com/portal"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-8 border-2 border-slate-200 transition-all duration-300 flex flex-col justify-between shadow-sm hover:shadow-2xl"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl text-emerald-700">
                    <i className="fa-solid fa-right-to-bracket" aria-hidden="true"></i>
                  </div>
                </div>
                
                <h3 className="font-header text-2xl font-bold text-slate-900 transition-colors mb-3">
                  Log ind
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 transition-colors">
                  Log ind på vores portal, og få overblik over dit forløb.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-emerald-700 transition-colors">
                <span>Log ind her</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

          </div>
        </div>
      </section>

      {/* INFINITE REVIEWS SLIDER SECTION */}
      <ReviewSlider />

      {/* MID-PAGE CALLOUT / QUICK FACTS */}
      <section className="py-20 bg-emerald-800 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            <div>
              <h2 className="font-header text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
                Tryghed og kvalitet frem for samlebåndsarbejde
              </h2>
              <p className="text-white text-base leading-relaxed mb-8">
                Hos Borgs Køreskole I/S vægter vi personlig kontakt og god dialog. Vi sikrer, at alle elever modtager den tid og opmærksomhed, der skal til for at blive en sikker og kompetent bilist.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-emerald-900/60 p-5 border border-emerald-700/50">
                  <div className="text-3xl font-extrabold font-header text-white mb-1">98%</div>
                  <div className="text-xs text-white">Glade &amp; tilfredse elever på Vestsjælland</div>
                </div>
                <div className="bg-emerald-900/60 p-5 border border-emerald-700/50">
                  <div className="text-3xl font-extrabold font-header text-white mb-1">29+</div>
                  <div className="text-xs text-white">Lovpligtige teorilektioner med fuld opfølgning</div>
                </div>
              </div>
            </div>

            <div className="bg-emerald-900 text-white p-8 sm:p-10 shadow-2xl border border-emerald-700/60 space-y-6">
              <h3 className="font-header text-2xl sm:text-3xl font-bold text-white mb-2">
                Klar til at tage kørekortet?
              </h3>
              <p className="text-white text-sm sm:text-base leading-relaxed">
                Udfyld vores korte kontaktformular eller ring til os direkte på <strong className="text-white underline underline-offset-4 decoration-emerald-400">21 47 01 87</strong> / <strong className="text-white underline underline-offset-4 decoration-emerald-400">23 20 04 25</strong> for tilmelding eller spørgsmål.
              </p>

              <div className="space-y-4 pt-2">
                <Link
                  to="/kontakt"
                  className="w-full py-4 px-6 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold flex items-center justify-center gap-2 text-base transition-all whitespace-nowrap"
                >
                  <span>Gå til tilmeldingsformular</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  )
}
